import { getAddress } from 'ethers'
import { SAFE_TX_SERVICE_URL } from './constants.js'
import type { Hex } from '../types.js'
import type { SafeTx } from './safe.js'

/**
 * Minimal client for the Safe Transaction Service public API. Anonymous
 * tier — no auth required. Used to fetch Safe state and propose new txs
 * from the browser.
 */

export interface SafeInfo {
  address: string
  nonce: number
  threshold: number
  owners: string[]
  modules: string[]
  version: string
}

export async function getSafeInfo(safe: string): Promise<SafeInfo> {
  const url = `${SAFE_TX_SERVICE_URL}/api/v1/safes/${getAddress(safe)}/`
  const resp = await fetch(url)
  if (!resp.ok) {
    throw new Error(`Safe Tx Service ${resp.status} fetching info: ${await resp.text()}`)
  }
  return (await resp.json()) as SafeInfo
}

export interface ProposeBody extends SafeTx {
  contractTransactionHash: Hex
  sender: string
  signature: Hex
  origin?: string
}

export interface MultisigTransaction {
  safeTxHash: string
  isExecuted: boolean
  isSuccessful: boolean | null
  transactionHash: string | null
  executionDate: string | null
  confirmations: Array<{ owner: string; signature: string }>
}

/**
 * 5s TTL cache. dApps poll wallet_getCallsStatus aggressively (~1Hz) and
 * each poll otherwise costs a round-trip to Safe Tx Service. The status
 * doesn't change faster than executions happen, so caching is safe.
 */
const txCache = new Map<
  string,
  { tx: MultisigTransaction | null; cachedAt: number }
>()
const TX_CACHE_TTL_MS = 5_000

export async function getMultisigTransaction(
  safeTxHash: string,
): Promise<MultisigTransaction | null> {
  const cached = txCache.get(safeTxHash)
  if (cached && Date.now() - cached.cachedAt < TX_CACHE_TTL_MS) {
    return cached.tx
  }
  const url = `${SAFE_TX_SERVICE_URL}/api/v1/multisig-transactions/${safeTxHash}/`
  const resp = await fetch(url)
  if (resp.status === 404) {
    txCache.set(safeTxHash, { tx: null, cachedAt: Date.now() })
    return null
  }
  if (!resp.ok) {
    throw new Error(
      `Safe Tx Service ${resp.status} fetching tx: ${await resp.text()}`,
    )
  }
  const tx = (await resp.json()) as MultisigTransaction
  txCache.set(safeTxHash, { tx, cachedAt: Date.now() })
  return tx
}

/**
 * Delete a pending (unexecuted) Safe transaction from the queue. Authenticated
 * by an EIP-712 `DeleteRequest` signature from a Safe owner — the wallet UI
 * has a "Delete from queue" button that emits exactly this. See Safe Tx
 * Service docs and `deleteRequestTypedData` for the payload shape.
 *
 * The Safe Tx Service silently swallows nonce gaps, but the Safe{Wallet} UI
 * only allows deleting the highest-nonce queued tx so the queue stays
 * contiguous. We follow the same convention in the caller.
 */
export async function deleteMultisigTx(
  safeTxHash: string,
  signature: string,
): Promise<void> {
  const url = `${SAFE_TX_SERVICE_URL}/api/v1/multisig-transactions/${safeTxHash}/`
  const resp = await fetch(url, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ signature }),
  })
  if (!resp.ok && resp.status !== 204) {
    throw new Error(
      `Safe Tx Service ${resp.status} on delete: ${await resp.text()}`,
    )
  }
}

export async function proposeTx(safe: string, body: ProposeBody): Promise<void> {
  const url = `${SAFE_TX_SERVICE_URL}/api/v1/safes/${getAddress(safe)}/multisig-transactions/`
  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...body,
      to: getAddress(body.to),
      gasToken: getAddress(body.gasToken),
      refundReceiver: getAddress(body.refundReceiver),
      sender: getAddress(body.sender),
    }),
  })
  if (!resp.ok && resp.status !== 201) {
    throw new Error(
      `Safe Tx Service ${resp.status} on propose: ${await resp.text()}`,
    )
  }
}
